import React, { useEffect, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Activity, Clock, Zap, AlertTriangle } from 'lucide-react';

const MGMT_URL = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL ||
  'https://nyqqynbtcrqdmhezmhsp.supabase.co/functions/v1/management';
const MGMT_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY || '';

interface HeartbeatPing {
  id: string;
  created_at: string;
  stage: string;
  reason: string;
  outcome: string;
  metadata: Record<string, unknown>;
}

interface AgentAction {
  id: string;
  created_at: string;
  action_type: string;
  slug: string | null;
  stage: string;
  reason: string;
  outcome: string;
  tier: number | null;
}

function ageLabel(isoTs: string): string {
  const ageMs = Date.now() - new Date(isoTs).getTime();
  const secs = Math.floor(ageMs / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

function statusFromAge(isoTs: string | null): 'healthy' | 'warning' | 'dead' {
  if (!isoTs) return 'dead';
  const ageSecs = (Date.now() - new Date(isoTs).getTime()) / 1000;
  if (ageSecs < 1200) return 'healthy'; // < 20 min
  if (ageSecs < 3600) return 'warning'; // < 1 hour
  return 'dead';
}

function ActionTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    retry_scrape: 'bg-blue-100 text-blue-800',
    retry_diff: 'bg-purple-100 text-purple-800',
    schedule_scrape: 'bg-cyan-100 text-cyan-800',
    escalate_human: 'bg-red-100 text-red-800',
    publish: 'bg-green-100 text-green-800',
    heartbeat_ping: 'bg-gray-100 text-gray-600',
    flag_archive_candidate: 'bg-orange-100 text-orange-800',
  };
  const cls = colors[type] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {type.replace(/_/g, ' ')}
    </span>
  );
}

export const AdminHeartbeatTab: React.FC = () => {
  const [pings, setPings] = useState<HeartbeatPing[]>([]);
  const [recentActions, setRecentActions] = useState<AgentAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pingsRes, actionsRes] = await Promise.all([
        fetch(`${MGMT_URL}/agent-actions?action_type=heartbeat_ping&limit=20`, {
          headers: { 'X-API-Key': MGMT_KEY },
        }),
        fetch(`${MGMT_URL}/agent-actions?limit=50&since=24h`, {
          headers: { 'X-API-Key': MGMT_KEY },
        }),
      ]);

      if (pingsRes.ok) {
        const data = await pingsRes.json();
        setPings(data.data ?? []);
      }
      if (actionsRes.ok) {
        const data = await actionsRes.json();
        // Exclude heartbeat_pings from the action log (shown separately above)
        setRecentActions((data.data ?? []).filter((a: AgentAction) => a.action_type !== 'heartbeat_ping'));
      }
    } catch (e) {
      console.error('AdminHeartbeatTab fetch error:', e);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60_000); // auto-refresh every 60s
    return () => clearInterval(interval);
  }, [fetchData]);

  const latestPing = pings[0] ?? null;
  const status = statusFromAge(latestPing?.created_at ?? null);

  const statusConfig = {
    healthy: { label: 'Healthy', color: 'bg-green-100 text-green-800', dot: 'bg-green-500', icon: Activity },
    warning: { label: 'Stale', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500', icon: Clock },
    dead: { label: 'No Signal', color: 'bg-red-100 text-red-800', dot: 'bg-red-500', icon: AlertTriangle },
  }[status];

  // Summarize action types from recent actions
  const actionCounts = recentActions.reduce<Record<string, number>>((acc, a) => {
    acc[a.action_type] = (acc[a.action_type] ?? 0) + 1;
    return acc;
  }, {});

  // Extract latest stats from most recent scheduling ping
  const schedulingPing = pings.find(p => p.stage === 'scheduling');
  const meta = (schedulingPing?.metadata ?? {}) as Record<string, number>;

  return (
    <div className="mt-4 space-y-6">
      {/* Status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`h-3 w-3 rounded-full ${statusConfig.dot} animate-pulse`} />
          <span className="text-lg font-semibold text-gray-800">Pipeline Heartbeat</span>
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusConfig.color}`}>
            <statusConfig.icon className="h-3 w-3" />
            {statusConfig.label}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          {lastRefresh && <span>Refreshed {ageLabel(lastRefresh.toISOString())}</span>}
          <Button size="sm" variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Ping</p>
          <p className="mt-1 text-xl font-bold text-gray-900">
            {latestPing ? ageLabel(latestPing.created_at) : '—'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">every 15 min target</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">T1 Outstanding</p>
          <p className="mt-1 text-xl font-bold text-gray-900">
            {meta.t1_scrape_outstanding ?? '—'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">scrapes pending today</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">T2 Outstanding</p>
          <p className="mt-1 text-xl font-bold text-gray-900">
            {meta.t2_outstanding ?? '—'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">scrapes pending this week</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actions (24h)</p>
          <p className="mt-1 text-xl font-bold text-gray-900">{recentActions.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">pipeline interventions</p>
        </div>
      </div>

      {/* Action type breakdown */}
      {Object.keys(actionCounts).length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-700">Actions in Last 24h</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(actionCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="flex items-center gap-1.5">
                  <ActionTypeBadge type={type} />
                  <span className="text-xs font-semibold text-gray-600">×{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Two-column layout: pings + recent actions */}
      <div className="grid grid-cols-2 gap-4">
        {/* Heartbeat ping history */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Recent Heartbeat Pings</h3>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
            </div>
          ) : pings.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">No heartbeat pings found</div>
          ) : (
            <div className="divide-y">
              {pings.map(ping => (
                <div key={ping.id} className="px-4 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">{ageLabel(ping.created_at)}</span>
                    <span className="text-xs text-gray-400">{ping.stage}</span>
                  </div>
                  <p className="text-xs text-gray-700 mt-0.5 truncate">{ping.reason}</p>
                  {ping.metadata?.actions_this_cycle !== undefined && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {ping.metadata.actions_this_cycle as number} actions · T1: {ping.metadata.t1_scrape_outstanding as number ?? '?'} · T2: {ping.metadata.t2_outstanding as number ?? '?'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent pipeline actions */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Recent Pipeline Actions (24h)</h3>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
            </div>
          ) : recentActions.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">No actions in last 24h</div>
          ) : (
            <div className="divide-y max-h-96 overflow-y-auto">
              {recentActions.map(action => (
                <div key={action.id} className="px-4 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <ActionTypeBadge type={action.action_type} />
                    <span className="text-xs text-gray-400 shrink-0">{ageLabel(action.created_at)}</span>
                  </div>
                  {action.slug && (
                    <p className="text-xs font-medium text-gray-700 mt-0.5">{action.slug}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{action.reason}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHeartbeatTab;
